import { ReactElement, ReactNode } from 'react';
import { createTheme, Theme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { deepmerge } from '@mui/utils';
import { theme as kingspanTheme } from '@kingspan/design-system-components';
import { useAppSelector } from 'redux/hooks';

function getTheme(division: string | null): Theme {
  const jorisidePrimary = {
    main: '#004065',
    dark: '#012a42',
  };

  const jorisideSecondary = {
    main: '#7495C1',
  };

  const primaryPalette = division === 'joriside' ? jorisidePrimary : kingspanTheme.palette.primary;
  const secondaryPalette = division === 'joriside' ? jorisideSecondary : kingspanTheme.palette.secondary;

  return createTheme(
    deepmerge(kingspanTheme, {
      typography: {
        fontFamily: ['KingspanBrownTT-Regular'],
        h1: {
          fontSize: '2.5rem',
          fontWeight: 700,
          letterSpacing: 'normal',
          lineHeight: 1.25,
        },
        h2: {
          fontSize: '2rem',
          fontWeight: 700,
          letterSpacing: 'normal',
          lineHeight: 1.25,
        },
        subtitle1: {
          color: '#9e9e9e',
          fontSize: '1rem',
          fontWeight: 400,
          letterSpacing: 'normal',
          lineHeight: 1.25,
        },
      },
      palette: {
        success: {
          light: '#c2e7d2',
        },
        error: {
          light: '#e7c2c2',
        },
        grey: {
          500: '#8C8C8C',
        },
        primary: primaryPalette,
        secondary: secondaryPalette,
      },
      components: {
        MuiChip: {
          styleOverrides: {
            root: {
              color: '#000000',
              cursor: 'inherit',
              '& span': {
                fontFamily: ['KingspanBrownTT-Light'],
              },
            },
          },
          variants: [
            {
              props: { color: 'default' },
              style: {
                backgroundColor: '#e0e8f1',
              },
            },
            {
              props: { size: 'small' },
              style: {
                height: '32px',
                '& span': {
                  padding: '0 6px',
                  fontSize: 12,
                },
              },
            },
            {
              props: { size: 'medium' },
              style: {
                padding: '20px 10px',
                borderRadius: '20px',
                '& span': {
                  fontSize: 16,
                },
              },
            },
          ],
        },
        MuiTabs: {
          styleOverrides: {
            root: {
              '& .MuiTab-root': { textTransform: 'none' },
              '& .MuiTab-root.Mui-selected': { color: 'black' },
              '& .MuiTabs-indicator': { background: 'black', height: 4, color: 'black' },
            },
          },
        },
        MuiSkeleton: {
          styleOverrides: {
            root: {
              transform: 'none',
            },
          },
        },
      },
    })
  );
}

interface Props {
  children: ReactNode;
}

function ThemeProvider({ children }: Props): ReactElement {
  const division = useAppSelector((state) => state.user.division);

  const panelsAndFasternersTheme = getTheme(division);

  return (
    <MuiThemeProvider theme={panelsAndFasternersTheme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
}

export default ThemeProvider;
